import { useQuery, useMutation } from "react-query";
import axios from "axios";
import { nuclearn_api_url } from "src/App";
import { NucToast } from "src/reusable/components/Toast";
import type { EnrichedArtifact } from "src/reusable/Chat/PromptInput/ArtifactSelectorModal";

// Moved out of Proposal Generation's services: every endpoint here is a
// generic dataset/artifact read that any workflow product's input-document
// flow needs (vnv-assist was already importing across products to get them).

export interface Artifact {
  id: number;
  filename: string;
  filetype: string;
  filesize: number;
  create_date: string;
  create_user_fk: number;
  sdr_id?: number;
  dataset_id?: number;
  dataset_name?: string;
}

export interface Dataset {
  id: number;
  name: string;
  description?: string;
  dataset_type_id: number;
}

/**
 * Search for artifacts by filename within a specific dataset.
 * Will just search if the artifact name includes the filename string.
 */
const searchDatasetArtifacts = async (
  datasetId: number,
  filename: string,
): Promise<Artifact[]> => {
  const response = await axios.get(
    `${nuclearn_api_url}/datasets/${datasetId}/artifacts/search`,
    {
      params: {
        filename: filename,
      },
    },
  );
  return response.data;
};

/**
 * Hook to search for artifacts by filename within a dataset
 */
export const useSearchDatasetArtifacts = () => {
  return useMutation(
    ({ datasetId, filename }: { datasetId: number; filename: string }) =>
      searchDatasetArtifacts(datasetId, filename),
    {
      onError: (error: any) => {
        console.error("Failed to search dataset artifacts:", error);
      },
    },
  );
};

/**
 * Search document datasets by name
 */
const searchDocumentDatasets = async (
  searchQuery: string,
): Promise<Dataset[]> => {
  // If no search query, get all document datasets
  if (!searchQuery) {
    const response = await axios.get(`${nuclearn_api_url}/datasets/`, {
      params: {
        dataset_type_id: 2, // Document type
        only_logically_deleted: false,
      },
    });
    return response.data;
  }

  // Use the search endpoint for searching datasets where name contains the search query
  const response = await axios.post(`${nuclearn_api_url}/datasets/search`, {
    search_query: searchQuery || null,
    filters: null,
    dataset_folder_id: null,
  });

  return response.data.results || response.data;
};

/**
 * Hook to search document datasets by name with debouncing support
 */
export const useSearchDocumentDatasets = (
  searchQuery: string,
  enabled: boolean = true,
) => {
  return useQuery(
    ["searchDocumentDatasets", searchQuery],
    () => searchDocumentDatasets(searchQuery),
    {
      enabled: enabled,
      staleTime: 2 * 60 * 1000, // 2 minutes
      cacheTime: 5 * 60 * 1000, // 5 minutes
      keepPreviousData: true, // Smooth transitions during search
    },
  );
};

/**
 * Processing status response from backend
 */
export interface ProcessingStatus {
  source_data_record_id: number;
  dataset_id: number;
  filename: string;
  status: string;
  display_status: string;
  is_processing: boolean;
  error_message?: string;
  progress_percentage: number;
  num_chunks: number;
  num_chunks_embedded: number;
  uploaded_at?: string;
  last_updated?: string;
}

/**
 * Display status for a selected artifact. An artifact whose SDR has no status
 * row yet is pending rather than completed; one with no SDR has nothing to
 * wait for.
 */
export const selectedArtifactProcessingState = (
  sdrId: number | null | undefined,
  statuses: ProcessingStatus[],
): {
  processingStatus: "pending" | "processing" | "completed" | "failed";
  processingProgress: number;
} => {
  if (typeof sdrId !== "number") {
    return { processingStatus: "completed", processingProgress: 100 };
  }
  const status = statuses.find((s) => s.source_data_record_id === sdrId);
  if (!status) return { processingStatus: "pending", processingProgress: 0 };
  return {
    processingStatus: status.error_message
      ? "failed"
      : status.is_processing
        ? "processing"
        : "completed",
    processingProgress: status.progress_percentage ?? 100,
  };
};

const getProcessingStatusBySdrIds = async (
  sdrIds: number[],
): Promise<ProcessingStatus[]> => {
  const response = await axios.get(
    `${nuclearn_api_url}/datasets/processing-status`,
    {
      params: {
        sdr_ids: sdrIds.join(","),
      },
    },
  );
  return response.data;
};

const isForbidden = (error: unknown) =>
  axios.isAxiosError(error) && error.response?.status === 403;

/**
 * Polls processing status for specific SDRs while any of them is processing.
 *
 * The request fails with 403 if the caller cannot read any one of the SDRs,
 * for example a document that was deleted or unshared after it was selected.
 * A failure shows an error toast, since the affected documents otherwise stay
 * pending with no explanation.
 */
export const useProcessingStatusBySdrIds = (
  sdrIds: number[],
  enabled: boolean = true,
) => {
  return useQuery(
    ["processingStatus", "sdrs", [...sdrIds].sort((a, b) => a - b)],
    () => getProcessingStatusBySdrIds(sdrIds),
    {
      enabled: enabled && sdrIds.length > 0,
      // A 403 does not change on retry.
      retry: (failureCount, error) => !isForbidden(error) && failureCount < 3,
      onError: (error) => {
        NucToast({
          message: isForbidden(error)
            ? "Couldn't check the processing status of your documents: one or more of them was deleted or is no longer shared with you. Remove it to continue."
            : "Couldn't check the processing status of your documents. Please try again.",
          type: "error",
          toastId: "processing-status-by-sdr-ids",
        });
      },
      refetchInterval: (data) => {
        // Keep refetching if any SDR is still processing
        const anyProcessing =
          data &&
          data.length > 0 &&
          data.some((status) => status.is_processing);
        return anyProcessing ? 2000 : false; // Poll every 2 seconds if processing
      },
      staleTime: 1000, // Consider stale quickly during processing
    },
  );
};

/**
 * Get artifacts by SDR ID
 * Used to fetch artifact ID after a file is uploaded and processed
 */
export const getArtifactsBySdrId = async (
  sdrId: number,
): Promise<Artifact[]> => {
  const response = await axios.get(`${nuclearn_api_url}/artifacts/`, {
    params: { sdr_id: sdrId },
  });
  return response.data;
};

/** One `/artifacts/enriched` row, with the defaults the panels rely on. */
const toEnrichedArtifact = (artifact: any): EnrichedArtifact => ({
  id: artifact.id,
  filename: artifact.filename || `Artifact ${artifact.id}`,
  filetype: artifact.filename?.split(".").pop() || "unknown",
  filesize: artifact.filesize || 0,
  create_date: artifact.create_date,
  create_user_fk: artifact.create_user_id,
  sdr_id: artifact.sdr_id,
  dataset_id: artifact.dataset_id || 0,
  dataset_name: artifact.dataset_name || "Unknown Dataset",
});

/**
 * Fetch enriched artifacts with dataset information
 * Uses the backend's enriched artifacts POST endpoint to avoid URL length limits
 */
export const fetchEnrichedArtifacts = async (
  artifactIds: number[],
): Promise<EnrichedArtifact[]> => {
  try {
    if (!artifactIds || artifactIds.length === 0) {
      console.error(
        "[fetchEnrichedArtifacts] ERROR: No artifact IDs provided! This should not happen.",
      );
      return [];
    }

    // Use POST endpoint to avoid URL length limitations with many artifact IDs
    const url = `${nuclearn_api_url}/artifacts/enriched`;
    const response = await axios.post(url, { artifact_ids: artifactIds });

    return response.data.map(toEnrichedArtifact);
  } catch (error) {
    console.error("Error fetching enriched artifacts:", error);
    throw error;
  }
};
